import { useState, type FormEvent } from 'react';
import { productOptions } from '@/mocks/home';

const ENDPOINT = 'https://readdy.ai/api/form/db8pf6rk13ra8mig8tq0';
type Status = 'idle' | 'sending' | 'success' | 'error';

const inputCls = 'w-full h-[54px] bg-background-50 border border-background-300/70 rounded-lg px-4 text-[15px] text-foreground-950 placeholder:text-foreground-500 hover:border-background-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-colors';
const labelCls = 'block whitespace-nowrap overflow-hidden text-ellipsis eyebrow !text-[11px] !tracking-[0.16em] text-foreground-700';

export const rfqSteps = [
  { t: 'Send your requirements', d: 'Share category, size, connection and quantity.' },
  { t: 'We review specifications', d: 'Our team checks fit, standards and options.' },
  { t: 'Receive a quotation', d: 'A clear quotation with lead time and terms.' },
];

export function familyToCategory(family: string): string {
  if (productOptions.includes(family)) return family;
  if (family === 'Actuators') return 'Actuators & Accessories';
  return 'Multiple / Not sure';
}

interface RFQFormProps {
  formId: string;
  idPrefix: string;
  defaultCategory?: string;
  product?: { slug: string; name: string } | null;
  className?: string;
}

export default function RFQForm({ formId, idPrefix, defaultCategory = '', product = null, className = '' }: RFQFormProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [msgLen, setMsgLen] = useState(0);
  const id = (k: string) => `${idPrefix}-${k}`;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'sending') return;
    const form = e.currentTarget;
    const fd = new FormData(form);
    const trap = String(fd.get('company_alt') || '').trim();
    if (trap) { setStatus('success'); return; }
    fd.delete('company_alt');

    const v = (k: string) => String(fd.get(k) || '').trim();
    const next: Record<string, string> = {};
    if (!v('name')) next.name = 'Please enter your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v('email'))) next.email = 'Please enter a valid business email.';
    if (!v('company')) next.company = 'Please enter your company.';
    if (!v('product_category')) next.product_category = 'Please select a category.';
    if (!v('message')) next.message = 'Please describe your requirements.';
    else if (v('message').length > 500) next.message = 'Maximum 500 characters.';
    setErrors(next);
    if (Object.keys(next).length) return;

    setStatus('sending');
    setFormError('');
    try {
      const body = new URLSearchParams();
      fd.forEach((val, key) => { if (String(val) !== '' || key !== 'product_slug') body.append(key, String(val)); });
      const response = await fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = /spam/i.test(serverMsg || '');
      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
        setMsgLen(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Submission failed. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error. Please check your connection and try again.');
    }
  };

  const err = (k: string) => errors[k] && <p id={id(`${k}-err`)} className="mt-1.5 text-xs text-primary-600">{errors[k]}</p>;
  const opt = <span className="text-foreground-500 normal-case tracking-normal"><span className="md:max-xl:hidden">(optional)</span><span className="hidden md:max-xl:inline">(opt.)</span></span>;

  return (
    <form id={formId} data-readdy-form noValidate onSubmit={onSubmit} className={`quote-form relative grid grid-cols-1 md:grid-cols-2 gap-x-7 gap-y-7 ${className}`}>
      <input type="text" name="company_alt" className="field-alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly onFocus={(e) => e.currentTarget.removeAttribute('readonly')} />
      {product && (
        <>
          <input type="hidden" name="product_slug" value={product.slug} />
          <input type="hidden" name="product_name" value={product.name} />
        </>
      )}
      <div>
        <label htmlFor={id('name')} className={labelCls}>Name *</label>
        <input id={id('name')} name="name" className={`${inputCls} mt-2`} aria-invalid={!!errors.name} aria-describedby={id('name-err')} />
        {err('name')}
      </div>
      <div>
        <label htmlFor={id('email')} className={labelCls}>Business Email *</label>
        <input id={id('email')} type="email" name="email" className={`${inputCls} mt-2`} aria-invalid={!!errors.email} aria-describedby={id('email-err')} />
        {err('email')}
      </div>
      <div>
        <label htmlFor={id('company')} className={labelCls}>Company *</label>
        <input id={id('company')} name="company" className={`${inputCls} mt-2`} aria-invalid={!!errors.company} aria-describedby={id('company-err')} />
        {err('company')}
      </div>
      <div>
        <label htmlFor={id('phone')} className={labelCls}>Phone {opt}</label>
        <input id={id('phone')} type="tel" name="phone" className={`${inputCls} mt-2`} />
      </div>
      <div>
        <label htmlFor={id('cat')} className={labelCls}>Product Category *</label>
        <div className="relative mt-2">
          <select id={id('cat')} name="product_category" defaultValue={defaultCategory} className={`${inputCls} appearance-none pr-10 cursor-pointer`} aria-invalid={!!errors.product_category} aria-describedby={id('product_category-err')}>
            <option value="" disabled>Select a category</option>
            {productOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-foreground-600" />
        </div>
        {err('product_category')}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-7 gap-y-7">
        <div>
          <label htmlFor={id('qty')} className={labelCls}>Quantity {opt}</label>
          <input id={id('qty')} name="quantity" className={`${inputCls} mt-2`} placeholder="e.g. 200 pcs" />
        </div>
        <div>
          <label htmlFor={id('size')} className={labelCls}>Size / Connection {opt}</label>
          <input id={id('size')} name="size_connection" className={`${inputCls} mt-2`} placeholder="e.g. DN50, flanged" />
        </div>
      </div>
      <div className="md:col-span-2">
        <label htmlFor={id('msg')} className={labelCls}>Requirements / Message *</label>
        <textarea id={id('msg')} name="message" rows={6} maxLength={500} onChange={(e) => setMsgLen(e.target.value.length)} className={`${inputCls} !h-auto py-4 mt-2 resize-none`} placeholder="Valve type, pressure rating, media, application..." aria-invalid={!!errors.message} aria-describedby={id('message-err')} />
        <div className="flex justify-between">{err('message') || <span />}<span className="mt-1.5 font-label text-[10px] text-foreground-500">{msgLen}/500</span></div>
      </div>
      <div className="md:col-span-2 flex flex-col-reverse md:flex-row md:items-center gap-4 justify-between">
        <div aria-live="polite" role="status" className="text-sm">
          {status === 'success' && <p className="text-secondary-800"><i className="ri-check-line text-primary-500 mr-1" />Thank you — your request has been received.</p>}
          {status === 'error' && <p className="text-primary-600">{formError}</p>}
        </div>
        <button type="submit" disabled={status === 'sending'} className="w-full md:w-auto whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed text-foreground-50 font-label text-xs tracking-[0.14em] uppercase h-[54px] px-10 rounded-md transition-colors group">
          {status === 'sending' ? 'Sending…' : 'Submit Request'} <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </form>
  );
}

export function RFQSteps({ className = '' }: { className?: string }) {
  return (
    <ol className={`mt-16 md:mt-20 pt-10 border-t border-background-200 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-7 ${className}`}>
      {rfqSteps.map((s, i) => (
        <li key={s.t}>
          <span className="eyebrow text-primary-500">0{i + 1}</span>
          <p className="mt-3 text-[16px] font-medium text-foreground-950">{s.t}</p>
          <p className="mt-1.5 text-sm text-foreground-600">{s.d}</p>
        </li>
      ))}
    </ol>
  );
}